(() => {
  let pending = false;

  async function loadPage(link, variant, signal) {
    const url = new URL(link.href);
    if (variant) url.searchParams.set('variant', variant);
    const response = await fetch(url, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error('Package page unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function findBundle(page, variant) {
    const template = page.querySelector(`template[data-package-variant="${variant}"]`);
    return (template?.content || page).querySelector('slikkepott-bundle[data-active="true"]');
  }

  function isBuyable(bundle) {
    return Boolean(bundle?.querySelector('.sp-add:not(.sp-add-available):not([disabled])'));
  }

  function packageItems(bundle, variant) {
    if (!isBuyable(bundle)) throw new Error('Package unavailable');
    const definition = JSON.parse(bundle.querySelector('.sp-definition').textContent);
    if ((definition.parentVariantId && definition.parentVariantId.split('/').pop() !== variant) ||
        !definition.components?.length || !definition.id || !definition.revision) {
      throw new Error('Package definition unavailable');
    }

    const group = crypto.randomUUID();
    return definition.components.map(component => ({
      id: component.variantId.split('/').pop(),
      quantity: component.quantity,
      properties: {
        'Del av produktpakke': definition.title,
        _sp_bundle: definition.id,
        _sp_revision: definition.revision,
        _sp_group: group,
        _sp_units: '1',
      },
    }));
  }

  function chooseVariant(page, link) {
    const options = page.querySelector('slikkepott-package-options .sp-variant-select');
    if (!options?.options.length) throw new Error('Package variants unavailable');

    const dialog = document.createElement('dialog');
    dialog.setAttribute('aria-label', 'Velg variant');
    dialog.style.cssText = 'width:min(92vw,28rem);padding:1.5rem;margin:auto;background:white;color:black;border:1px solid #ddd;border-radius:12px;max-height:85vh;overflow:auto';
    dialog.innerHTML = `
      <form method="dialog" class="grid gap-3">
        <h2>Velg variant</h2>
        <label>Pakkevariant <select name="variant" class="w-full" style="min-height:44px;font-size:16px;padding:8px"></select></label>
        <p role="status"></p>
        <button class="btn btn--primary" value="add">Legg pakken i handlekurven</button>
        <button class="btn btn--secondary" value="cancel">Lukk</button>
      </form>`;
    const select = dialog.querySelector('select');
    const summary = dialog.querySelector('[role="status"]');
    const add = dialog.querySelector('[value="add"]');
    for (const option of options.options) {
      const bundle = findBundle(page, option.value);
      const price = bundle?.querySelector('.sp-price-current')?.textContent.trim();
      const label = [option.textContent.trim(), price].filter(Boolean).join(' · ');
      select.add(new Option(isBuyable(bundle) ? label : `${label} · Utsolgt`, option.value));
    }
    select.value = options.value;
    function updateSelection() {
      const bundle = findBundle(page, select.value);
      add.disabled = !isBuyable(bundle);
      summary.textContent = bundle?.querySelector('.sp-inventory')?.textContent.trim() ||
        'Denne varianten er utilgjengelig.';
    }
    select.addEventListener('change', updateSelection);
    updateSelection();
    document.body.append(dialog);
    return new Promise(resolve => {
      dialog.addEventListener('close', () => {
        const selected = dialog.returnValue === 'add' ? select.value : null;
        dialog.remove();
        link.focus();
        resolve(selected);
      }, { once: true });
      dialog.showModal();
    });
  }

  function setLoading(link, loading) {
    if (loading) link.setAttribute('aria-busy', 'true');
    else link.removeAttribute('aria-busy');
    link.classList.toggle('btn--loading', loading);
  }

  document.addEventListener('click', async event => {
    const link = event.target.closest('a[data-package-quick-add], a[data-package-variants]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (pending) return;

    pending = true;
    setLoading(link, true);
    const status = link.parentElement.querySelector('[data-package-quick-status]');
    status.hidden = false;
    status.textContent = link.hasAttribute('data-package-variants') ? 'Henter varianter …' : 'Legger i handlekurven …';
    const root = window.Shopify?.routes?.root || '/';
    const controller = new AbortController();
    let timeout = setTimeout(() => controller.abort(), 15000);
    let submitted = false;

    try {
      let variant = link.dataset.packageQuickAdd;
      const page = await loadPage(link, variant, controller.signal);
      if (!variant) {
        clearTimeout(timeout);
        status.hidden = true;
        setLoading(link, false);
        variant = await chooseVariant(page, link);
        if (!variant) return;
        setLoading(link, true);
        status.hidden = false;
        status.textContent = 'Legger i handlekurven …';
        timeout = setTimeout(() => controller.abort(), 15000);
      }
      const items = packageItems(findBundle(page, variant), variant);
      const drawer = document.querySelector('cart-drawer');
      const pubsub = window.FoxTheme?.pubsub;
      const sections = [];
      if (drawer && pubsub && typeof drawer.show === 'function') {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }),
        );
      }
      const payload = { items };
      if (sections.length) {
        payload.sections = [...new Set(sections)].slice(0, 5);
        payload.sections_url = window.location.pathname;
      }

      submitted = true;
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart add was not confirmed');

      const added = await response.json();
      if (!added.sections?.[drawer?.getAttribute('data-section-id')]) {
        window.location.assign(`${root}cart`);
        return;
      }
      const cart = typeof window.fetchCartData === 'function'
        ? await window.fetchCartData(true)
        : await (await fetch(`${root}cart.js`, { signal: controller.signal })).json();
      cart.sections = added.sections;
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: added } }));
      drawer.show(link);
      status.textContent = 'Pakken er lagt i handlekurven.';
    } catch {
      // After submission, check the cart rather than risking a duplicate add.
      window.location.assign(submitted ? `${root}cart` : link.href);
    } finally {
      clearTimeout(timeout);
      setLoading(link, false);
      pending = false;
    }
  });
})();
